<template>
  <!--
    MK3 variant — clean Tailwind flex row, no Bootstrap navbar
    wrappers. Mounts in the AppShell's filter strip and lays out as
    a single horizontal line. Reuses every store/handler from the
    legacy path below.
  -->
  <div
    v-if="useMk3 && show"
    class="tw-flex tw-flex-wrap tw-items-center tw-gap-3"
  >
    <div v-if="show.hierarchy" class="tw-flex tw-items-center tw-gap-2">
      <!--
        Dropdown width reacts to the sidebar state:
          - Sidebar COLLAPSED: 21rem (preferred wider size; works
            when the topbar has the extra ~13rem of horizontal real
            estate the open sidebar would otherwise eat).
          - Sidebar OPEN: 14rem (the original size — keeps the
            filter row from wrapping below ~1440px viewports).
        Without this, two 21rem dropdowns + datepicker + Location +
        the right-side notifications/user-menu group overflow the
        available width and the date picker wraps onto a second row.
      -->
      <!--
        Width is responsive at two layers:
          1. Sidebar state — collapsed gives the topbar ~13rem
             more horizontal real estate, so the dropdowns can
             go wider without forcing the date picker to wrap.
          2. Viewport size — on 2xl+ (1536px and up) the
             dropdowns grow further so the filter row uses the
             available width on large monitors instead of
             leaving the laptop-sized widths frozen.
      -->
      <div
        :class="uiStore.isSidebarCollapsed
          ? 'tw-w-[21rem] 2xl:tw-w-[26rem]'
          : 'tw-w-56 2xl:tw-w-[20rem]'"
      >
        <UiSearchSelect
          :options="hierarchyTopData"
          :model-value="selectedNodeId"
          placeholder="All organisations"
          :clearable="hierarchyTopData.length > 1"
          @update:model-value="onNodeSelect"
          @clear="clearNode"
        />
      </div>
      <!--
        Width is responsive at two layers:
          1. Sidebar state — collapsed gives the topbar ~13rem
             more horizontal real estate, so the dropdowns can
             go wider without forcing the date picker to wrap.
          2. Viewport size — on 2xl+ (1536px and up) the
             dropdowns grow further so the filter row uses the
             available width on large monitors instead of
             leaving the laptop-sized widths frozen.
      -->
      <div
        :class="uiStore.isSidebarCollapsed
          ? 'tw-w-[21rem] 2xl:tw-w-[26rem]'
          : 'tw-w-56 2xl:tw-w-[20rem]'"
      >
        <treeselect
          :max-height="700"
          placeholder="All"
          value-format="object"
          :options="hierarchyNodeData"
          :disabled="!hierarchyNodeData || hierarchyNodeData.length === 0"
          :model-value="filters.childNode"
          @update:model-value="onChildNodeChange"
        />
      </div>
    </div>

    <!--
      Force the DatePicker's input text to align left. The component
      ships with a centered/right-aligned default; the :deep selector
      reaches into its scoped styles without breaking encapsulation
      elsewhere.
    -->
    <div v-if="show.datepicker" class="mk3-datepicker-wrap">
      <DatePicker
        :initial-value="filters.dateRange"
        :confirm="onDateSelect"
      />
    </div>

    <UiButton
      v-if="show.location"
      variant="primary"
      @click="locationModalVisible = true"
    >
      Location
    </UiButton>

    <!--
      Subtle global toggle: prints value labels onto dashboard charts so
      agents can screenshot them for customer reports (TAMK3-28). Drives the
      UI-store flag every labelled chart reads; ml-auto parks it at the far
      right of the filter row, away from the data filters.
    -->
    <button
      v-if="hasCharts"
      type="button"
      class="tw-ml-auto tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-border tw-px-3 tw-py-1.5 tw-text-sm tw-cursor-pointer tw-transition-colors"
      :class="uiStore.showDataLabels
        ? 'tw-border-blue-500 tw-bg-blue-50 tw-text-blue-700'
        : 'tw-border-gray-300 tw-bg-white tw-text-gray-500 hover:tw-text-gray-700'"
      :aria-pressed="uiStore.showDataLabels"
      title="Show data values on charts — handy for screenshots in customer reports"
      @click="uiStore.toggleDataLabels()"
    >
      <i class="fa fa-tags" />
      <span>Data labels</span>
    </button>

    <UiModal
      v-if="locationModalVisible"
      v-model="locationModalVisible"
      title="Location"
      class="location-modal"
      hide-footer
      no-close-on-backdrop
      size="xl"
    >
      <p> Use the following options to filter data within Travel Analytics. </p>
      <CountryFilter
        ref="country"
        :submit-handler="onLocationSelect"
        :initial-value="filters.country.map(filter => ({...filter}))"
        @submit="hidelocationModal"
      />
    </UiModal>
  </div>

  <!--
    Legacy MK2 variant — Bootstrap navbar-nav. Kept verbatim so the
    existing MK2 shell renders unchanged.
  -->
  <b-navbar-nav v-else-if="show">
    <li class="nav-item d-none d-lg-flex">
      <form class="form-inline header-filters">
        <div class="hierarchy-selection" v-if="show.hierarchy">
          <div class="hierarchy-dropdowns">
            <div class="hierarchy-dropdown">
              <UiSearchSelect
                :options="hierarchyTopData"
                :model-value="selectedNodeId"
                placeholder="All organisations"
                :clearable="hierarchyTopData.length > 1"
                @update:model-value="onNodeSelect"
                @clear="clearNode"
              />
            </div>

            <div class="hierarchy-dropdown">
              <treeselect
                :max-height="700"

                placeholder="All"
                value-format="object"

                :options="hierarchyNodeData"

                :disabled="!hierarchyNodeData || hierarchyNodeData.length === 0"
                :model-value="filters.childNode"
                @update:model-value="onChildNodeChange"
              />
            </div>
          </div>
        </div>

        <div v-if="show.datepicker" class="header-filter-item header-datepicker">
          <DatePicker
            :initial-value="filters.dateRange"
            :confirm="onDateSelect"
          />
        </div>

        <div v-if="show.location" class="header-filter-item">
          <UiButton
            variant="primary"
            @click="locationModalVisible = true"
          >
            Location
          </UiButton>
        </div>
      </form>
    </li>

    <!-- Small view elements -->
    <li class="nav-item d-lg-none">
      <form class="form-inline">
        <UiButton
          @click="filterModalVisible = true"
          variant="secondary"
          class="mx-1"
          v-if="show.hierarchy || show.datepicker"
        >
          <i class="fa fa-filter" />
        </UiButton>
        <UiButton
          class="mx-1"
          @click="locationModalVisible = true"
          v-if="show.location"
        >
          <i class="fa fa-globe" />
        </UiButton>
      </form>
    </li>

    <UiModal
      v-if="locationModalVisible"
      v-model="locationModalVisible"
      title="Location"
      class="location-modal"
      hide-footer
      no-close-on-backdrop
      size="xl"
    >
      <p> Use the following options to filter data within Travel Analytics. </p>
      <CountryFilter
        ref="country"
        :submit-handler="onLocationSelect"
        :initial-value="filters.country.map(filter => ({...filter}))"
        @submit="hidelocationModal"
      />
    </UiModal>

    <UiModal
      v-if="filterModalVisible"
      v-model="filterModalVisible"
      title="Filter"
      id="filterModal"
    >
      <b-form>
        <b-form-group label="Customer" v-if="show.hierarchy">
          <div class="px-3">
            <UiSearchSelect
              :options="hierarchyTopData"
              :model-value="selectedNodeId"
              placeholder="All organisations"
              :clearable="hierarchyTopData.length > 1"
              @update:model-value="onNodeSelect"
              @clear="clearNode"
            />
          </div>
        </b-form-group>

        <b-form-group>
          <div class="px-3 mobile">
            <treeselect
              :max-height="700"

              placeholder="All"
              value-format="object"

              :options="hierarchyNodeData"
              :disabled="!hierarchyNodeData || hierarchyNodeData.length === 0"
              :model-value="filters.childNode"
              @update:model-value="onChildNodeChange"
            />
          </div>
        </b-form-group>

        <b-form-group label="Date Range" v-if="show.datepicker">
          <DatePicker
            :initial-value="filters.dateRange"
            :confirm="onDateSelect"
          />
        </b-form-group>
      </b-form>
    </UiModal>
  </b-navbar-nav>
</template>

<script setup lang="ts">
  import { ref, computed, watch, nextTick } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { storeToRefs } from 'pinia'
  import Treeselect from '@zanmato/vue3-treeselect'

  import { useHeaderStore } from '@/stores/header'
  import { useUiStore } from '@/stores/ui'
  import { deleteCache } from '@/utils/request'

  import DatePicker from "@/components/DatePicker"
  import CountryFilter from "@/components/CountryFilter"

  const route = useRoute()
  const router = useRouter()
  const headerStore = useHeaderStore()
  const uiStore = useUiStore()

  // Pick the MK3 template branch when the app shell is on MK3.
  // Falls back to the legacy Bootstrap navbar markup otherwise so
  // the existing MK2 navbar layout is unchanged.
  const { useMk3Shell: useMk3 } = storeToRefs(uiStore)

  const {
    filterDataGetter: filters,
    hierarchyTopDataGetter: hierarchyTopData,
    hierarchyNodeDataGetter: hierarchyNodeData,
  } = storeToRefs(headerStore)

  // Modal visibility
  const locationModalVisible = ref(false)
  const filterModalVisible = ref(false)

  // Computed
  const show = computed(() => {
    const { header = {} } = route.meta as Record<string, any>
    const { hide = [] } = header as Record<string, any>
    if (hide === true) return false
    return {
      hierarchy: hide.indexOf('hierarchy') === -1,
      datepicker: hide.indexOf('datepicker') === -1,
      location: hide.indexOf('location') === -1
    }
  })

  // Only dashboards that actually carry (labelled) charts show the data-labels
  // toggle (TAMK3-28) — gated by the `hasCharts` route-meta flag set in
  // menu.ts, so it doesn't appear on chart-less pages (Budgets, Scheduled
  // Reports, etc.).
  const hasCharts = computed(() => !!(route.meta as Record<string, any>)?.hasCharts)

  // UiSearchSelect's v-model is a scalar id; the filter store keeps
  // the full {id, label} payload so other consumers (treeselect child
  // dropdown, page filters) can read both.
  const selectedNodeId = computed(() => filters.value.node?.id ?? null)

  // beforeCreate equivalent - runs during setup
  useHeaderStore().getHierarchyTopData()
    .then(data => onHierarchyLoad(data))

  // Watchers
  watch(show, (val) => {
    useHeaderStore().setHeaderDisplayed(val)
  }, { immediate: true })

  watch(() => filters.value.node, (node, old) => {
    if (old === node) return
    if (node && node.id) {
      useHeaderStore().getHierarchyNodeData(node.id)
      .then(() => {
        const headerStore = useHeaderStore()
        const selectedChildNode = headerStore.filterDataGetter.childNode
        const childNodes = headerStore.hierarchyNodeDataGetter
        if (!selectedChildNode || !childNodes || !childNodes.some(row => row.id === selectedChildNode.id)) {
          headerStore.setChildNode(undefined)
        }
      })
    }
  }, { immediate: true })

  // Methods
  function onNodeSelect (idOrNode) {
    // UiSearchSelect emits a scalar id. Bootstrap path also feeds this
    // function via `onHierarchyLoad` with a raw store row that already
    // has `{value, text}` shape — handle both for back-compat.
    if (idOrNode === null || idOrNode === undefined) return clearNode()
    const id = typeof idOrNode === 'object'
      ? (idOrNode.id ?? idOrNode.value)
      : idOrNode
    if (!id) return clearNode()
    const option = hierarchyTopData.value.find(o => o.value === id)
    if (!option) return clearNode()
    useHeaderStore().setNode({ id: option.value, label: option.text })
  }

  function clearNode () {
    if (filters.value.node === undefined) return
    deleteCache()
    useHeaderStore().setChildNode(undefined)
    useHeaderStore().setNode(undefined)
    // Force re-fetch by replacing the current route
    nextTick(() => {
      router.replace({ path: route.path, query: { t: Date.now() } })
    })
  }

  function onChildNodeChange (node) {
    deleteCache()
    useHeaderStore().setChildNode(node || undefined)
    nextTick(() => {
      router.replace({ path: route.path, query: { t: Date.now() } })
    })
  }

  function onDateSelect (dateRange) {
    useHeaderStore().setDateRange(dateRange)
  }

  function onLocationSelect (country) {
    // Bust the response cache so dashboard pages' watchers re-invoke
    // their store fetchers and hit the network with the new `countries`
    // param applied (otherwise axios-cache-interceptor returns cached
    // responses for previously-seen URL+param combos).
    deleteCache()
    useHeaderStore().setCountryFilter(country)
  }

  function hidelocationModal () {
    locationModalVisible.value = false
  }

  function onHierarchyLoad (nodes) {
    if (nodes && nodes.length === 1) {
      onNodeSelect(nodes[0])
    }
  }
</script>

<style lang="scss">
  // Header filter bar layout — Bootstrap 5 dropped .form-inline, so we must
  // declare display:flex ourselves (b-nav-form previously did this for us).
  .header-filters {
    display: flex;
    flex-wrap: nowrap !important;
    flex: 1 1 auto;
    align-items: center;
    gap: 0.75rem;
    padding: 0 0.75rem;
  }
  // Also override the <li> wrapper to not wrap
  .app-header .navbar-nav > li.d-flex {
    flex-wrap: nowrap;
    flex: 1 1 auto;
  }
  .header-filter-item {
    flex-shrink: 0;
  }
  // Date range input needs ~250px to fit "DD/MM/YYYY - DD/MM/YYYY" without truncation.
  .header-datepicker {
    width: 250px;
    .dp__main { width: 100%; }
  }
  // Ensure primary button text is white in navbar context
  .header-filters .btn-primary {
    color: #fff !important;
  }
  .location-modal .modal .modal-dialog {
    max-width: 1000px !important;
  }
  .hierarchy-selection {
    flex: 1 1 auto;
    max-width: 1000px;
    min-width: 0;

    .hierarchy-dropdowns {
      display: flex;
      gap: 0.75rem;
    }
    .hierarchy-dropdown {
      flex: 1 1 0;
      position: relative;
      min-width: 0;
    }

    /* Org names can be long; let the popover expand beyond the narrow
       trigger so the user can read full labels. UiSearchSelect's
       popover defaults to trigger width — this overrides for the
       header-filter context only. */
    .ui-search-select__popover {
      min-width: 400px;
    }
    .vue-treeselect__placeholder {
      top: unset;
    }
  }
  .mobile .vue-treeselect {
    margin-left: unset !important;
  }

  // MK3-only: push DatePicker input text to the left edge. The
  // component centres / right-aligns its label by default; for the
  // filter strip we want it flush-left so it aligns with the
  // dropdowns next to it. Selector is scoped via .mk3-datepicker-wrap
  // so we don't disturb the legacy MK2 navbar appearance.
  .mk3-datepicker-wrap {
    input,
    .form-control,
    .mx-input,
    button {
      text-align: left !important;
    }
  }
</style>
